import {
  Button,
  Card,
  CardContent,
  CardMedia,
  Container,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select
} from "@mui/material";
import React, { useEffect, useState } from "react";
import ProductInsertUpdateModal from "./productInsertUpdateModal";
import { getPrimeProductList } from "src/api/primestockapi";
import {
  AddCircleOutline,
  EditSharp,
  HistorySharp,
  PersonAddAlt,
  TrendingUp
} from "@mui/icons-material";
import StockInsertUpdateModal from "../StockIn/stockInsertUpdateModal";
import ProductHistory from "./productHistory";
import AssignProductModal from "./assignProductModal";
import { Routes } from "src/constant/api_url";
import { useNavigate, useLocation } from "react-router-dom";
import CustomLoader from "src/components/CustomLoader";
import {
  canAddStock,
  canAssignStock,
  canManageCatalogue,
  getLoggedInUser
} from "src/utils/stockPermission";

export default function Index() {
  const navigate = useNavigate();
  const location = useLocation();

  // Prime panel roles: Main Admin (u_type 3, is_subadmin 0),
  // Sub Admin (u_type 3, is_subadmin 1), Staff (u_type 2). No city tier.
  const user = getLoggedInUser();
  const canManage = canManageCatalogue(user);
  const canAssign = canAssignStock(user);
  const canStockIn = canAddStock(user);

  const [addEditModal, setAddEditModal] = useState(false);
  const [stockAddModal, setStockAddModal] = useState(false);
  const [modalType, setModalType] = useState("Add");
  const [productList, setProductList] = useState([]);
  const [rowData, setRowData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [productHistoryPage, setProductHistoryPage] = useState(false);
  const [assignProductModal, setAssignProductModal] = useState(false);
  const [filterData, setFilterData] = useState({ search: "", p_type: -1 });

  const toggleAddEditModal = () => setAddEditModal(!addEditModal);
  const toggleStockAddModal = () => setStockAddModal(!stockAddModal);
  const toggleProductHistoryPage = () => setProductHistoryPage(!productHistoryPage);
  const toggleAssignProductModal = () => setAssignProductModal(!assignProductModal);

  const handleGetProducts = async (filterdata) => {
    setLoader(true);
    const resp = await getPrimeProductList({
      ...filterdata
    });
    setLoader(false);
    if (resp.status == 1) {
      setProductList(resp?.data?.data);
    }
  };

  useEffect(() => {
    handleGetProducts(filterData);
  }, []);

  // Support direct navigation from Dashboard → open Product History
  useEffect(() => {
    if (location.state?.autoOpenHistory && location.state?.p_id) {
      setRowData({
        p_id: location.state.p_id,
        p_name: location.state.p_name,
        p_image: location.state.p_image,
        p_type: location.state.p_type,
        ...location.state
      });
      setProductHistoryPage(true);
    }
  }, [location.state]);

  return (
    <>
      {!productHistoryPage ? (
        <Container>
          <Grid container spacing={2}>
            <Grid item xs={2}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h4" gutterBottom>
                  Products
                </Typography>
              </Stack>
            </Grid>
            <Grid
              item
              xs={10}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 2
              }}
            >
              <FormControl size="small" sx={{ width: 200 }}>
                <InputLabel id="product-type-label">Product Type</InputLabel>
                <Select
                  labelId="product-type-label"
                  id="product-type-select"
                  value={filterData.p_type}
                  label="Product Type"
                  onChange={(e) => {
                    setFilterData({ ...filterData, p_type: e.target.value });
                  }}
                >
                  <MenuItem value={-1}>All</MenuItem>
                  <MenuItem value={0}>Product</MenuItem>
                  <MenuItem value={1}>Pin</MenuItem>
                  <MenuItem value={2}>Welcome Kit</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Search"
                name="search"
                autoComplete="off"
                sx={{ width: 250 }}
                onChange={(e) => {
                  setFilterData({ ...filterData, search: e.target.value });
                }}
                value={filterData.search || ""}
              />
              <Button variant="contained" onClick={() => handleGetProducts(filterData)}>
                Search
              </Button>
              <Button
                variant="contained"
                color="inherit"
                onClick={() => {
                  handleGetProducts({ search: "", p_type: -1 });
                  setFilterData({ search: "", p_type: -1 });
                }}
              >
                Clear
              </Button>
              {canManage && (
                <Button
                  onClick={() => {
                    toggleAddEditModal();
                    setModalType("Add");
                  }}
                  variant="contained"
                >
                  Add Product
                </Button>
              )}
            </Grid>
          </Grid>

          <Grid container spacing={3} marginTop="10px">
            {loader ? (
              <CustomLoader />
            ) : productList?.length > 0 ? (
              productList.map((item) => {
                return (
                  <Grid item xs={12} sm={6} md={4} lg={4} key={item.p_id}>
                    <Card
                      sx={{
                        position: "relative",
                        borderRadius: 3,
                        border: "1px solid #e2e8f0",
                        boxShadow: "0 1px 3px rgba(15,23,42,0.06), 0 1px 2px rgba(15,23,42,0.04)",
                        backgroundColor: "#fff",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          boxShadow: "0 4px 12px rgba(15,23,42,0.08), 0 2px 4px rgba(15,23,42,0.06)",
                          transform: "translateY(-2px)",
                          borderColor: "#cbd5e1"
                        },
                        overflow: "hidden"
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        p={2}
                        sx={{ cursor: "pointer" }}
                        onClick={() => {
                          navigate("/pininmemberlist", { state: { ...item } });
                        }}
                      >
                        {/* Left: Product Image */}
                        <CardMedia
                          component="img"
                          image={
                            item.p_image
                              ? `${Routes.API_HOSTNAME}prime-products-image/${item.p_image}`
                              : "https://dummyimage.com/200x200/cccccc/000000&text=Product"
                          }
                          alt={item.p_name}
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: 2,
                            objectFit: "contain",
                            backgroundColor: "#fff"
                          }}
                        />

                        {/* Right: Product Info */}
                        <CardContent
                          sx={{
                            flex: 1,
                            padding: "0 !important",
                            wordBreak: "break-word"
                          }}
                        >
                          <Typography variant="subtitle1" fontWeight={700} color="#212B36">
                            {item.p_name}
                          </Typography>

                          <Typography
                            variant="body2"
                            color="#212B36"
                            mt={0.1}
                            sx={{ fontSize: "0.80rem" }}
                          >
                            Current Stock:{" "}
                            <strong style={{ color: "#212B36" }}>{item.p_stock_qty ?? 0}</strong>
                          </Typography>

                          {/* <Typography
                            variant="body2"
                            color="#212B36"
                            mt={0.1}
                            sx={{ fontSize: "0.80rem" }}
                          >
                            Return Stock:{" "}
                            <strong style={{ color: "#212B36" }}>{item.p_return_qty ?? 0}</strong>
                          </Typography> */}

                          <Typography
                            variant="body2"
                            color="#212B36"
                            mt={0.1}
                            sx={{ fontSize: "0.80rem" }}
                          >
                            Eligible Member:{" "}
                            <strong style={{ color: "#212B36" }}>
                              {item.member_eligibility_counter ?? 0}
                            </strong>
                          </Typography>

                          <Typography
                            variant="body2"
                            color="#212B36"
                            mt={0.1}
                            sx={{ fontSize: "0.80rem" }}
                          >
                            Assign Member:{" "}
                            <strong style={{ color: "#212B36" }}>
                              {item.member_assign_counter ?? 0}
                            </strong>
                          </Typography>
                        </CardContent>
                      </Stack>

                      {/* Bottom: Action Buttons */}
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        px={2}
                        sx={{
                          borderTop: "1px solid #d6e2ef",
                          backgroundColor: "#f8fbff"
                        }}
                      >
                        {canManage ? (
                          <>
                            <Tooltip title="Edit Product">
                              <IconButton
                                color="warning"
                                onClick={() => {
                                  setModalType("Edit");
                                  setRowData(item);
                                  toggleAddEditModal();
                                }}
                              >
                                <EditSharp />
                              </IconButton>
                            </Tooltip>
                            {canStockIn && (
                              <Tooltip title="Add Stock">
                                <IconButton
                                  color="success"
                                  onClick={() => {
                                    setModalType("Add");
                                    setRowData(item);
                                    toggleStockAddModal();
                                  }}
                                >
                                  <AddCircleOutline />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        ) : (
                          ""
                        )}
                        {canAssign && (
                          <Tooltip title="Assign Guest">
                            <IconButton
                              color="primary"
                              onClick={() => {
                                setRowData(item);
                                setAssignProductModal(true);
                              }}
                            >
                              <TrendingUp />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canAssign && (
                          <Tooltip title="Assign Member">
                            <IconButton
                              color="primary"
                              onClick={() => {
                                navigate("/pininmemberlist", { state: { ...item } });
                              }}
                            >
                              <PersonAddAlt />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="View History">
                          <IconButton
                            color="info"
                            onClick={() => {
                              setRowData(item);
                              toggleProductHistoryPage();
                            }}
                          >
                            <HistorySharp />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Card>
                  </Grid>
                );
              })
            ) : (
              <Grid item xs={12}>
                <Typography variant="h6" color="text.secondary" textAlign="center" mt={4}>
                  No products available.
                </Typography>
              </Grid>
            )}
          </Grid>
        </Container>
      ) : (
        <ProductHistory
          productHistoryPage={productHistoryPage}
          setProductHistoryPage={setProductHistoryPage}
          rowData={rowData}
        />
      )}
      {addEditModal && (
        <ProductInsertUpdateModal
          addEditModal={addEditModal}
          setAddEditModal={setAddEditModal}
          toggleAddEditModal={toggleAddEditModal}
          rowData={rowData}
          setRowData={setRowData}
          modalType={modalType}
          handleGetProducts={handleGetProducts}
        />
      )}

      {stockAddModal && (
        <StockInsertUpdateModal
          addEditModal={stockAddModal}
          setAddEditModal={setStockAddModal}
          toggleAddEditModal={toggleStockAddModal}
          modalType={modalType}
          rowData={rowData}
          handleGetProducts={handleGetProducts}
          filterData={filterData}
        />
      )}

      {assignProductModal && (
        <AssignProductModal
          assignProductModal={assignProductModal}
          setAssignProductModal={setAssignProductModal}
          toggleAssignProductModal={toggleAssignProductModal}
          rowData={rowData}
          handleGetProducts={handleGetProducts}
        />
      )}
    </>
  );
}
